'use client';

import type { UpdateUserBody, User } from '@api/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateUser as updateUserService } from '@website/services/users.service';

import { syncUserTypeCookie } from './useCurrentUser';

interface UseUpdateUserOptions {
  /** Enable optimistic updates for instant UI feedback */
  optimistic?: boolean;
  /** Callback on successful update */
  onSuccess?: (user: User) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for updating user profile.
 * Automatically syncs with useCurrentUser cache on success.
 *
 * @example
 * const updateUser = useUpdateUser({
 *   onSuccess: () => toast.success('Profile updated!')
 * });
 *
 * updateUser.mutate({ id: user.id, firstName: 'John' });
 *
 * @example With optimistic updates
 * const updateUser = useUpdateUser({
 *   optimistic: true,
 *   onError: () => toast.error('Failed to update')
 * });
 */
export function useUpdateUser(options: UseUpdateUserOptions = {}) {
  const { optimistic = false, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserBody & { id: string }) => updateUserService(data),

    // Optimistic update - instantly update UI before server responds
    onMutate: optimistic
      ? async (newData) => {
          // Cancel outgoing refetches
          await queryClient.cancelQueries({ queryKey: ['user'] });

          // Snapshot previous value
          const previousUser = queryClient.getQueryData<User | null>(['user']);

          // Optimistically update cache with only the fields being updated
          if (previousUser) {
            const { id: _id, ...updateFields } = newData;
            queryClient.setQueryData<User>(['user'], {
              ...previousUser,
              ...updateFields
            } as User);
          }

          return { previousUser };
        }
      : undefined,

    // Rollback on error if using optimistic updates
    onError: (error, _variables, context) => {
      if (optimistic && context?.previousUser) {
        queryClient.setQueryData(['user'], context.previousUser);
      }
      onError?.(error as Error);
    },

    // Refetch to ensure server state on success
    onSuccess: async (updatedUser) => {
      // IMPORTANT: Sync user_type cookie SYNCHRONOUSLY before navigation
      // This prevents race condition where middleware reads stale cookie value
      // (useCurrentUser's useEffect would update cookie async, after navigation starts)
      syncUserTypeCookie(updatedUser.isProfessional);

      // Update cache IMMEDIATELY with the mutation result
      // This ensures page protections (useRequireAuth, AdminLayoutClient) see the new
      // user data right away, preventing race conditions that cause 401 redirects
      queryClient.setQueryData(['user'], updatedUser);

      // Call success callback with updated cache ready
      // Navigation will see the correct isProfessional value immediately
      onSuccess?.(updatedUser);

      // Refetch to get authoritative server state (may have other fields updated)
      await queryClient.refetchQueries({ queryKey: ['user'] });

      // Updating user fields can trigger `reevaluateProfessionalStatus` on the
      // backend (firstName/lastName/mobileNumber/bio/profilePictureKey/
      // verificationDocumentKey are all gates for the incomplete→pending flip).
      // Invalidate the professional cache so `useCurrentProfessional` and any
      // CTA hook reading from it (e.g. `useUserCTAs`) re-fetch the new status
      // immediately rather than waiting up to 30s of staleTime. Broad
      // invalidation matches `currentProfessionalQueryKey` regardless of
      // userId/locale to keep call sites simple.
      if (updatedUser.isProfessional) {
        queryClient.invalidateQueries({ queryKey: ['professional', 'private'] });
      }
    }
  });
}
